import React, { useMemo, useState, useCallback } from "react"
import { FlatList, TouchableOpacity, StyleSheet } from "~/ui/primitives"
import { useSafeAreaInsets } from "~/ui/safe-area"
import styled, { useTheme } from "~/ui/styled"
// import { observer } from "mobx-react-lite"
import { SearchChatContentScreenProps} from "./types"
import { SearchBar } from "~components/SearchBar"
import { BetweenFlex, ColumnFlex, StartFlex, Text } from "~components/El"
import { useStores } from "~models"
import useNavigation from "~hooks/useNavigation"
import { MessageTypes } from "~proto"
import Avatar from "~widgets/avatar/Avatar"
import { formatMessageTime } from "~utils/format"

const Container = styled(ColumnFlex)`
  flex: 1;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

const SearchBox = styled(ColumnFlex)`
  width: 100%;
  height: auto;
  padding: 10px 12px;
`

type Row = any

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

const SearchChatContent: React.FC<SearchChatContentScreenProps> = ({ chat_cid }) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const { chatStore } = useStores()
  const current = chatStore.current

  const [q, setQ] = useState("")

  const rows: Row[] = useMemo(() => {
    const query = q.trim().toLowerCase()

    if (!current) return []

    // Safety: ensure we're searching the expected chat.
    const target = String(chat_cid)
    if (String(current.chat_cid) !== target) return []

    const messages: any[] = current.allMessages || []

    const matched = messages.filter((m) => {
      if (!m) return false
      if (m.type !== MessageTypes.TEXT) return false
      const content = (m.content || "").toString()
      if (!content) return false
      if (query && !content.toLowerCase().includes(query)) return false
      return true
    })

    matched.sort((a, b) => {
      const ta = typeof a.created?.getTime === "function" ? a.created.getTime() : Number(a.created || 0)
      const tb = typeof b.created?.getTime === "function" ? b.created.getTime() : Number(b.created || 0)
      return tb - ta
    })

    return matched
  }, [q, chat_cid, current])

  const onPressRow = useCallback(
    (message: any) => {
      navigation.navigate("Chat", {
        chat_id: current?.chat_id ?? chat_cid,
        scrollToMessageId: message?.id ?? message?.message_id,
      })
    },
    [chat_cid, current?.chat_id, navigation],
  )

  const renderHighlightedContent = useCallback(
    (content: string) => {
      const query = q.trim()
      if (!query) return content
      const parts = content.split(new RegExp(`(${escapeRegExp(query)})`, "ig"))
      return parts.map((part, idx) => {
        const hit = part.toLowerCase() === query.toLowerCase()
        if (!hit) return <Text key={idx}>{part}</Text>
        return (
          <Text key={idx} color="primaryText" fontWeight="700">
            {part}
          </Text>
        )
      })
    },
    [q],
  )

  const renderItem = useCallback(
    ({ item }: { item: Row }) => {
      const senderName = item?.sender?.name || ""
      const senderAvatar = item?.sender?.avatar || ""
      return (
        <ColumnFlex onClick={() => onPressRow(item)}>
          <BetweenFlex width="100%" padding="12px" bg={theme.colors.blockBackground} alignItems="flex-start">
            <StartFlex gap="10px" flex={1}>
              <Avatar avatar={senderAvatar} name={senderName} size={40} />
              <ColumnFlex flex={1} gap="4px">
                <Text color="text" fontWeight="600" numberOfLines={1}>
                  {senderName}
                </Text>
                <Text color="secondaryText" numberOfLines={1}>
                  {renderHighlightedContent(String(item.content || ""))}
                </Text>
              </ColumnFlex>
            </StartFlex>
            <Text color="secondaryText" fontSize="12px">
              {formatMessageTime(item.created) || ""}
            </Text>
          </BetweenFlex>
          <ColumnFlex width="100%" height={StyleSheet.hairlineWidth} bg={theme.colors.border} />
        </ColumnFlex>
      )
    },
    [onPressRow, renderHighlightedContent, theme.colors.border],
  )
  return (
    <Container>
      <SearchBox>
        <SearchBar
          value={q}
          placeholder="搜索聊天内容"
          onChange={setQ}
          onClear={() => setQ("")}
        />
      </SearchBox>

      <FlatList
        style={{ flex: 1, width: "100%" }}
        contentContainerStyle={{ paddingBottom: insets.bottom }}
        data={rows}
        keyExtractor={(item) => item.message_id || String(item.id)}
        renderItem={renderItem}
        keyboardShouldPersistTaps="handled"
      />
    </Container>
  )
}

export default SearchChatContent

